import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { io, type Socket } from "socket.io-client";
import useAutenticacaoJwt from "@auth/services/jwt/useAutenticacaoJwt";
import { getGrupoAtualAutenticacaoSpaceApi, getTokenJwtSpaceApi } from "@/api/space/sessaoAutenticacao.space";
import { env } from "@/configs/configEnv/env";
import { WebSocketContext } from "./WebSocketContext";
import type {
	WebSocketContextType,
	WebSocketEmit,
	WebSocketEventHandler,
	WebSocketStatus,
	WebSocketSubscribe,
} from "./types";

const SOCKET_IO_URL = env.VITE_SOCKET_IO_URL ?? "";

type ListenerRegistry = Map<string, Set<WebSocketEventHandler>>;

type WebSocketProviderProps = {
	readonly children: ReactNode;
};

function attachRegisteredListeners(socket: Socket, listeners: ListenerRegistry): void {
	listeners.forEach((handlers, eventName) => {
		handlers.forEach((handler) => {
			socket.on(eventName, handler);
		});
	});
}

export function WebSocketProvider({ children }: WebSocketProviderProps) {
	const { isAuthenticated } = useAutenticacaoJwt();
	const socketRef = useRef<Socket | null>(null);
	const listenersRef = useRef<ListenerRegistry>(new Map());
	const [status, setStatus] = useState<WebSocketStatus>("idle");

	const subscribe = useCallback<WebSocketSubscribe>((eventName, handler) => {
		const eventHandler = handler as WebSocketEventHandler;
		const handlers = listenersRef.current.get(eventName) ?? new Set<WebSocketEventHandler>();

		handlers.add(eventHandler);
		listenersRef.current.set(eventName, handlers);
		socketRef.current?.on(eventName, eventHandler);

		return () => {
			handlers.delete(eventHandler);

			if (handlers.size === 0) listenersRef.current.delete(eventName);

			socketRef.current?.off(eventName, eventHandler);
		};
	}, []);

	const emit = useCallback<WebSocketEmit>((eventName, ...args) => {
		socketRef.current?.emit(eventName, ...args);
	}, []);

	useEffect(() => {
		if (!isAuthenticated) {
			setStatus("idle");
			return undefined;
		}

		const token = getTokenJwtSpaceApi();
		const groupId = getGrupoAtualAutenticacaoSpaceApi();

		if (!SOCKET_IO_URL || !token || !groupId) {
			setStatus("idle");
			return undefined;
		}

		let closingIntentionally = false;
		setStatus("connecting");

		const socket = io(SOCKET_IO_URL, {
			transports: ["websocket"],
			reconnection: true,
			auth: {
				token,
				groupId,
			},
		});

		socketRef.current = socket;
		attachRegisteredListeners(socket, listenersRef.current);

		socket.on("connect", () => {
			setStatus("connected");
		});

		socket.on("connect_error", (error) => {
			setStatus("error");
			console.error("Erro na conexão Socket.IO global.", error);
		});

		socket.on("disconnect", (reason) => {
			if (closingIntentionally) return;

			setStatus("disconnected");
			console.error(`Conexão Socket.IO global encerrada. Motivo: ${reason}.`);
		});

		return () => {
			closingIntentionally = true;
			socket.disconnect();

			if (socketRef.current === socket) socketRef.current = null;
		};
	}, [isAuthenticated]);

	const value = useMemo<WebSocketContextType>(
		() => ({
			status,
			subscribe,
			emit,
		}),
		[emit, status, subscribe],
	);

	return <WebSocketContext value={value}>{children}</WebSocketContext>;
}
